import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as practiceUtils from "practiceUtils";
import * as textToSpeech from "textToSpeech";
import PracticePlayTTSView from "views/PracticePlayTTSView";
import TransliterationView from "./TransliterationView";

interface Props {
  clozeSentence: practiceUtils.ClozeSentence;
  mode:
    | {
        type: "blanked-out";
      }
    | {
        type: "complete";
        ttsLanguage?: string;
        transliterationLanguage?: string;
      };
}

const PracticeContextView: preact.FunctionalComponent<Props> = ({
  clozeSentence,
  mode,
}: Props) => {
  /**
   * The maximum number of characters to display. Slightly restricted on mobile to avoid
   * overflowing off the bottom of the screen.
   */
  const maxLength = deviceDetect.isSmall() ? 360 : 9999;

  const firstTargetPartIndex = clozeSentence.findIndex(
    (part) => part.type === "target"
  );
  const lastTargetPartIndex = _.findLastIndex(
    clozeSentence,
    (part) => part.type === "target"
  );

  /** The length between and including the first and last target parts. */
  const essentialLength = clozeSentence.reduce((length, part, index) => {
    if (index >= firstTargetPartIndex && index <= lastTargetPartIndex) {
      return length + part.text.length;
    }
    return length;
  }, 0);

  const maxSurrounding = Math.floor((maxLength - essentialLength) / 2);
  const trimmedClozeSentence = clozeSentence.map((part) => _.clone(part));

  if (
    trimmedClozeSentence[0].type === "normal" &&
    trimmedClozeSentence[0].text.length > maxSurrounding
  ) {
    trimmedClozeSentence[0].text =
      "..." +
      endOfStringToNearestWordBoundary(
        trimmedClozeSentence[0].text,
        maxSurrounding
      );
  }

  const lastIndex = trimmedClozeSentence.length - 1;
  if (
    trimmedClozeSentence[lastIndex].type === "normal" &&
    trimmedClozeSentence[lastIndex].text.length > maxSurrounding
  ) {
    trimmedClozeSentence[lastIndex].text =
      startOfStringToNearestWordBoundary(
        trimmedClozeSentence[lastIndex].text,
        maxSurrounding
      ) + "...";
  }

  const maxBlankCharacters = 30;

  return (
    <>
      {trimmedClozeSentence.map((part) => {
        switch (part.type) {
          case "normal":
            return part.text;
          case "target":
            return (
              <strong class="transliterationContainer">
                {mode.type === "complete" && mode.transliterationLanguage ? (
                  <TransliterationView
                    text={part.text}
                    language={mode.transliterationLanguage}
                  />
                ) : null}
                <div>
                  {mode.type === "complete" &&
                  mode.ttsLanguage &&
                  textToSpeech.languageSupported(mode.ttsLanguage) ? (
                    <PracticePlayTTSView
                      text={part.text}
                      language={mode.ttsLanguage}
                    />
                  ) : null}
                  {mode.type === "blanked-out"
                    ? part.text
                        .substring(0, maxBlankCharacters)
                        .replace(/./g, "_")
                    : part.text}
                </div>
              </strong>
            );
        }
      })}
    </>
  );
};

/**
 * Returns the @param length characters at the end of the @param input string, but including
 * extra characters as needed to reach the nearest word boundary.
 */
const startOfStringToNearestWordBoundary = (input: string, length: number) => {
  const endIndex = input.indexOf(" ", length);
  if (endIndex === -1) {
    // Didn't find a space, so just return the length of string requested
    return input.substring(0, length);
  }

  return input.substring(0, endIndex);
};

const endOfStringToNearestWordBoundary = (input: string, length: number) => {
  // Reverse input
  const reversedInput = input.split("").reverse().join("");

  // Get the start of the string to the nearest word boundary
  const reversedStart = startOfStringToNearestWordBoundary(
    reversedInput,
    length
  );

  // Reverse again
  return reversedStart.split("").reverse().join("");
};

export default PracticeContextView;
